import { ChangeDetectorRef, Directive, Inject, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import produce from 'immer';
import { Observable } from 'rxjs';

import { AccaBoostLadderItem } from '../../../reward-tokens/reward-tokens.model';
import { NOTIFICATION_LADDER_VISIBLE } from '../../services/digital/acca-boost-notification-sync.service';

export interface ILadderItem extends AccaBoostLadderItem {
    selected: boolean;
    included: boolean;
}

@Directive()
export class BaseAccaBoostLadderCarouselComponent {
    @Input() pickCount: number;
    @Input() items: AccaBoostLadderItem[] = [];

    vm: ILadderItem[];
    offset = 0;
    endReached: boolean;
    private carouselCenter = 3;

    constructor(@Inject(NOTIFICATION_LADDER_VISIBLE) notificationAnimationDone$: Observable<void>, changeRef: ChangeDetectorRef) {
        notificationAnimationDone$.pipe(takeUntilDestroyed()).subscribe(() => {
            this.animationDone();

            changeRef.markForCheck();
        });
    }

    mapModel(): ILadderItem[] {
        const lastIndex = this.items.length - 1;

        return this.items.map((item, index) => {
            const isLastItem = index === lastIndex;

            return this.mapLadderItem(item, isLastItem);
        });
    }

    trackItem(index: number): number {
        return index;
    }

    animationDone(): void {
        this.vm = this.updateModel();
    }

    updateModel(): ILadderItem[] {
        const reset = (item: ILadderItem) => {
            item.selected = false;
            item.included = item.numberOfLegs < this.pickCount;
        };

        return produce(this.vm, (draft) => {
            draft.forEach(reset);

            const selectedItem = this.findSelectedItem(draft, this.pickCount);
            selectedItem.selected = true;
            selectedItem.included = false;
        });
    }

    private mapLadderItem(leg: AccaBoostLadderItem, isLast: boolean): ILadderItem {
        return {
            ...leg,
            included: leg.numberOfLegs < this.pickCount && !isLast,
            selected: false,
        };
    }

    private findSelectedItem<TItem extends AccaBoostLadderItem>(items: TItem[], count: number): TItem {
        return items.reduce((previous, current) => {
            return current.numberOfLegs <= count ? current : previous;
        });
    }

    setCarouselProps(): boolean {
        const lastItem = this.items[this.items.length - 1];
        this.endReached = this.pickCount > lastItem.numberOfLegs;

        const newOffset = this.endReached ? this.calculateMaxOffset() : this.calculateOffset();
        const offsetChanged = newOffset !== this.offset;

        this.offset = newOffset;

        return offsetChanged;
    }

    private calculateMaxOffset(): number {
        return this.items.length - 1;
    }

    private calculateOffset(): number {
        const selectedItem = this.findSelectedItem(this.items, this.pickCount);
        const selectedIndex = this.items.indexOf(selectedItem);

        return Math.max(selectedIndex - this.carouselCenter + 1, 0);
    }
}
